import { nextTick, onBeforeUnmount, type Ref, watch } from "vue";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

export function useDialogFocus(
  open: Ref<boolean>,
  dialog: Ref<HTMLElement | null>,
  close: () => void
): void {
  let previousFocus: HTMLElement | null = null;

  function focusableElements(): HTMLElement[] {
    if (!dialog.value) return [];
    return Array.from(dialog.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (element) => !element.hidden && element.getAttribute("aria-hidden") !== "true"
    );
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (!open.value) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;

    const elements = focusableElements();
    if (!elements.length) {
      event.preventDefault();
      dialog.value?.focus();
      return;
    }

    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  watch(
    open,
    async (isOpen) => {
      if (isOpen) {
        previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        document.addEventListener("keydown", handleKeydown);
        await nextTick();
        (focusableElements()[0] || dialog.value)?.focus();
      } else {
        document.removeEventListener("keydown", handleKeydown);
        previousFocus?.focus();
        previousFocus = null;
      }
    },
    { flush: "post" }
  );

  onBeforeUnmount(() => document.removeEventListener("keydown", handleKeydown));
}
